import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ViewBar from './ViewBar';
import VotingSystem from '../components/VotingSystem';
import { collection, getDocs, addDoc, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Function to get all marketing bucket list items
const getMarketingBucketList = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'marketingBucketList'));
    const buckets: any[] = [];
    
    querySnapshot.forEach((doc) => {
      buckets.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return buckets;
  } catch (error) {
    console.error('Error getting marketing bucket list:', error);
    return [];
  }
};

// Function to create a new marketing bucket list item
const createNewMarketingBucket = async (bucketData: any) => {
  try {
    const bucketRef = await addDoc(collection(db, 'marketingBucketList'), {
      ...bucketData,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    return bucketRef.id;
  } catch (error) {
    console.error('Error creating new marketing bucket:', error);
    return null;
  }
};

// Function to delete a marketing bucket list item
const deleteMarketingBucket = async (bucketId: string) => {
  try {
    const bucketRef = doc(db, 'marketingBucketList', bucketId);
    await deleteDoc(bucketRef);

    console.log(`Marketing bucket ${bucketId} deleted successfully`);
    return true;
  } catch (error) {
    console.error('Error deleting marketing bucket:', error);
    return false;
  }
};

// Function to move a bucket item to marketing plan collection
const moveToMarketingPlan = async (bucketData: any) => {
  try {
    // Create the plan document
    const planData = {
      name: bucketData.name,
      description: bucketData.description,
      expectedBudget: bucketData.expectedBudget || 0,
      status: bucketData.status || 'open',
      expectedScope: bucketData.expectedScope || 'mid',
      expectedReturn: bucketData.expectedReturn || 'mid',
      files: bucketData.files || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add to marketing collection
    const planRef = await addDoc(collection(db, 'marketingPlan'), planData);

    // Delete from bucket list
    const bucketRef = doc(db, 'marketingBucketList', bucketData.id);
    await deleteDoc(bucketRef);

    console.log(`Marketing bucket ${bucketData.id} moved to plan ${planRef.id} successfully`);
    return planRef.id;
  } catch (error) {
    console.error('Error moving bucket to marketing plan:', error);
    return null;
  }
};

interface AllMarketingBucketListProps {
  onBucketSelect: (bucketId: string, bucketName: string) => void;
}

const AllMarketingBucketList: React.FC<AllMarketingBucketListProps> = ({ onBucketSelect }) => {
  const [marketingBuckets, setMarketingBuckets] = useState<any[]>([]);
  const [showCreateBucket, setShowCreateBucket] = useState(false);
  const [newBucketName, setNewBucketName] = useState('');
  const [newBucketDescription, setNewBucketDescription] = useState('');
  const [newBucketExpectedBudget, setNewBucketExpectedBudget] = useState('');
  const [newBucketExpectedScope, setNewBucketExpectedScope] = useState('mid');
  const [newBucketExpectedReturn, setNewBucketExpectedReturn] = useState('mid');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bucketToDelete, setBucketToDelete] = useState<{ id: string; name: string } | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [bucketToMove, setBucketToMove] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'vertical'>('grid');
  const [statusFilter, setStatusFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [returnFilter, setReturnFilter] = useState('');

  const handleDeleteBucket = async () => {
    if (!bucketToDelete) return;
    
    setLoading(true);
    const success = await deleteMarketingBucket(bucketToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setBucketToDelete(null);
      await loadMarketingBuckets(); // Reload buckets
    }
    setLoading(false);
  };

  const openDeleteDialog = (bucketId: string, bucketName: string) => {
    setBucketToDelete({ id: bucketId, name: bucketName });
    setDeleteDialogOpen(true);
  };

  const openMoveDialog = (bucket: any) => {
    setBucketToMove(bucket);
    setMoveDialogOpen(true);
  };

  const handleMoveToPlan = async () => {
    if (!bucketToMove) return;
    
    setLoading(true);
    const planId = await moveToMarketingPlan(bucketToMove);
    if (planId) {
      setMoveDialogOpen(false);
      setBucketToMove(null);
      await loadMarketingBuckets(); // Reload buckets
    }
    setLoading(false);
  };

  const handleCreateBucket = async () => {
    if (!newBucketName.trim()) return;
    
    setLoading(true);
    const bucketData = {
      name: newBucketName,
      description: newBucketDescription,
      expectedBudget: parseFloat(newBucketExpectedBudget) || 0,
      expectedScope: newBucketExpectedScope,
      expectedReturn: newBucketExpectedReturn,
      status: 'open',
      files: []
    };
    
    const bucketId = await createNewMarketingBucket(bucketData);
    if (bucketId) {
      setNewBucketName('');
      setNewBucketDescription('');
      setNewBucketExpectedBudget('');
      setNewBucketExpectedScope('mid');
      setNewBucketExpectedReturn('mid');
      setShowCreateBucket(false);
      await loadMarketingBuckets(); // Reload buckets
      onBucketSelect(bucketId, newBucketName); // Select the new bucket
    }
    setLoading(false);
  };

  // Load marketing buckets
  const loadMarketingBuckets = async () => {
    try {
      const buckets = await getMarketingBucketList();
      console.log('Loaded buckets:', buckets);
      setMarketingBuckets(buckets);
    } catch (error) {
      console.error('Error loading marketing buckets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data from Firebase on component mount
  useEffect(() => {
    loadMarketingBuckets();
  }, []);

  const handleBucketSelect = (bucketId: string, bucketName: string) => {
    console.log('Bucket selected:', bucketId, bucketName);
    const displayName = bucketName || `Bucket ${bucketId}`;
    onBucketSelect(bucketId, displayName);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'in discussion': return 'warning';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'high': return 'error';
      case 'mid': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getReturnColor = (returnValue: string) => {
    switch (returnValue) {
      case 'high': return 'success';
      case 'mid': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  // Filter marketing buckets based on selected filters
  const filteredMarketingBuckets = marketingBuckets.filter(bucket => {
    const statusMatch = !statusFilter || bucket.status === statusFilter;
    const scopeMatch = !scopeFilter || bucket.expectedScope === scopeFilter;
    const returnMatch = !returnFilter || bucket.expectedReturn === returnFilter;
    return statusMatch && scopeMatch && returnMatch;
  });

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading marketing bucket list...</Typography>
      </div>
    );
  }

    return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <ViewBar 
        view={viewMode} 
        onViewChange={setViewMode} 
        onCreateNew={() => setShowCreateBucket(true)}
        createButtonText="Add New Idea"
        showFilters={true}
        statusFilter={statusFilter}
        scopeFilter={scopeFilter}
        returnFilter={returnFilter}
        onStatusFilterChange={setStatusFilter}
        onScopeFilterChange={setScopeFilter}
        onReturnFilterChange={setReturnFilter}
        statusOptions={[
          { value: 'open', label: 'Open' },
          { value: 'in discussion', label: 'In Discussion' },
          { value: 'accepted', label: 'Accepted' },
          { value: 'declined', label: 'Declined' }
        ]}
      />
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8" style={{ marginTop: '60px' }}>
        <Grid container spacing={3} sx={{
          ...(viewMode === 'vertical' && {
            flexDirection: 'column',
            maxWidth: '800px',
            margin: '0 auto',
            '& .MuiGrid-item': {
              maxWidth: '100%',
              flexBasis: '100%'
            }
          })
        }}>
          {filteredMarketingBuckets.map((bucket) => (
            <Grid item xs={12} sm={6} md={4} key={bucket.id}>
              <Card 
                elevation={2} 
                sx={{ 
                  height: '280px', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { 
                    elevation: 4,
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleBucketSelect(bucket.id, bucket.name)}
              >
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative' }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 0.5
                  }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openMoveDialog(bucket);
                      }}
                      sx={{ 
                        color: 'success.main',
                        '&:hover': { 
                          backgroundColor: 'success.light',
                          color: 'white'
                        }
                      }}
                    >
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteDialog(bucket.id, bucket.name || `Bucket ${bucket.id}`);
                      }}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': { 
                          backgroundColor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {bucket.name || `Bucket ${bucket.id}`}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {bucket.description || 'No description'}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Expected Budget:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bucket.expectedBudget ? `$${bucket.expectedBudget.toLocaleString()}` : 'Not set'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip 
                          label={bucket.status || 'open'} 
                          color={getStatusColor(bucket.status || 'open')}
                          size="small"
                        />
                        <Chip 
                          label={`Scope: ${bucket.expectedScope || 'mid'}`} 
                          color={getScopeColor(bucket.expectedScope || 'mid')}
                          size="small"
                        />
                        <Chip 
                          label={`Return: ${bucket.expectedReturn || 'mid'}`} 
                          color={getReturnColor(bucket.expectedReturn || 'mid')}
                          size="small"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <VotingSystem 
                          collectionName="marketingBucketList" 
                          documentId={bucket.id} 
                          size="small"
                          showCounts={true}
                          showTooltips={true}
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '280px', 
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'divider',
                transition: 'all 0.2s',
                '&:hover': { 
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => setShowCreateBucket(true)}
            >
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Add New Idea
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {showCreateBucket && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <Card sx={{ p: 4, maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Add New Marketing Idea
              </Typography>
              <TextField
                fullWidth
                label="Idea Name"
                value={newBucketName}
                onChange={(e) => setNewBucketName(e.target.value)}
                sx={{ mb: 3 }}
                autoFocus
              />
              <TextField
                fullWidth
                label="Description"
                value={newBucketDescription}
                onChange={(e) => setNewBucketDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Expected Budget (USD)"
                type="number"
                value={newBucketExpectedBudget}
                onChange={(e) => setNewBucketExpectedBudget(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Expected Scope</Typography>
                  <select
                    value={newBucketExpectedScope}
                    onChange={(e) => setNewBucketExpectedScope(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="low">Low</option>
                    <option value="mid">Mid</option>
                    <option value="high">High</option>
                  </select>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Expected Return</Typography>
                  <select
                    value={newBucketExpectedReturn}
                    onChange={(e) => setNewBucketExpectedReturn(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="low">Low</option>
                    <option value="mid">Mid</option>
                    <option value="high">High</option>
                  </select>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setShowCreateBucket(false);
                  setNewBucketName('');
                  setNewBucketDescription('');
                  setNewBucketExpectedBudget('');
                  setNewBucketExpectedScope('mid');
                  setNewBucketExpectedReturn('mid');
                }}>
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleCreateBucket}
                  disabled={!newBucketName.trim()}
                >
                  Create
                </Button>
              </Box>
            </Card>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setBucketToDelete(null);
          }}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">
            Delete Marketing Idea
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{bucketToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteDialogOpen(false);
                setBucketToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBucket}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete Idea'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Move to Plan Confirmation Dialog */}
        <Dialog
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false);
            setBucketToMove(null);
          }}
          aria-labelledby="move-dialog-title"
        >
          <DialogTitle id="move-dialog-title">
            Move to Marketing Plan
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to move "{bucketToMove?.name}" from the bucket list to a marketing plan?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMoveDialogOpen(false);
                setBucketToMove(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleMoveToPlan}
              color="success"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Moving...' : 'Move to Plan'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AllMarketingBucketList; 